// EU Disease Service
// EU Decision 2018/945 integration service
// Part of Policy Framework & EU Diseases Implementation

import { db } from '../firebase';
import { 
  collection, 
  doc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  Timestamp 
} from 'firebase/firestore';
import euDiseasesData from '../data/eu-notifiable-diseases-2018-945.json';

// Export as default for easier importing

class EUDiseaseService {
  /**
   * Map EU diseases to existing biological entities
   */
  async mapEUDiseasesToEntities(organizationId) {
    try {
      console.log('Starting EU disease mapping...');
      
      const results = {
        matched: [],
        created: [],
        failed: []
      };

      for (const euDisease of euDiseasesData.diseases) {
        try {
          // Try to find existing entity by scientific name
          const existingEntity = await this.findMatchingEntity(
            organizationId,
            euDisease.scientificName,
            euDisease.diseaseName
          );

          if (existingEntity) {
            // Update existing entity with EU data
            await this.updateEntityWithEUData(existingEntity.id, euDisease);
            results.matched.push({
              entityId: existingEntity.id,
              entityName: existingEntity.scientificName,
              euDiseaseCode: euDisease.diseaseCode
            });
          } else {
            // Create new entity (marked as example)
            const newEntityId = await this.createEntityFromEUDisease(
              organizationId,
              euDisease
            );
            results.created.push({
              entityId: newEntityId,
              euDiseaseCode: euDisease.diseaseCode
            });
          }
        } catch (error) {
          results.failed.push({
            diseaseCode: euDisease.diseaseCode,
            diseaseName: euDisease.diseaseName,
            error: error.message
          });
        }
      }

      console.log('EU disease mapping complete:', results);
      return results;
    } catch (error) {
      console.error('Error mapping EU diseases:', error);
      throw error;
    }
  }

  /**
   * Find matching biological entity
   */
  async findMatchingEntity(organizationId, scientificName, commonName) {
    try {
      const entitiesRef = collection(db, 'biological_entities');
      
      // Search by scientific name
      let q = query(
        entitiesRef,
        where('organizationId', '==', organizationId),
        where('scientificName', '==', scientificName)
      );
      
      let snapshot = await getDocs(q);
      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }

      // Search by common name
      q = query(
        entitiesRef,
        where('organizationId', '==', organizationId),
        where('commonName', '==', commonName)
      );
      
      snapshot = await getDocs(q);
      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      }

      return null;
    } catch (error) {
      console.error('Error finding matching entity:', error);
      throw error;
    }
  }

  /**
   * Update existing entity with EU data
   */
  async updateEntityWithEUData(entityId, euDisease) {
    try {
      const entityRef = doc(db, 'biological_entities', entityId);
      
      const updateData = {
        // EU FLAGS
        euNotifiable: true,
        euDiseaseCode: euDisease.diseaseCode,
        euDecision: "2018/945",
        
        // CASE DEFINITION
        euCaseDefinition: euDisease.caseDefinition || null,
        
        // REPORTING
        euReportingDeadline: euDisease.reportingDeadline,
        euReportingCriteria: euDisease.reportingCriteria || null,
        
        // SURVEILLANCE
        euSurveillanceSystem: euDisease.surveillanceSystem || "TESSy",
        ecdcCode: euDisease.ecdcCode,
        
        // IMPORTANCE
        euPublicHealthImportance: euDisease.publicHealthImportance,
        euCrossBorderThreat: euDisease.crossBorderThreat || null,
        euEpidemicPotential: euDisease.epidemicPotential || null,
        
        // TRANSMISSION
        euTransmissionMode: euDisease.transmissionMode || [],
        euIncubationPeriod: euDisease.incubationPeriod || null,
        
        // CONTROL MEASURES
        euIsolationRequired: euDisease.isolationRequired || false,
        euContactTracingRequired: euDisease.contactTracingRequired || false,
        euPreventionMeasures: euDisease.preventionMeasures || [],
        
        // RPN MULTIPLIERS
        euRpnMultiplier: euDisease.rpnBaseMultiplier,
        euAlertThresholdMultiplier: euDisease.alertThresholdMultiplier,
        euOutbreakThreshold: euDisease.outbreakThreshold || null,
        
        // SEASONAL
        euSeasonalPattern: euDisease.seasonalPattern || null,
        
        // METADATA
        euDataSource: "EU Decision 2018/945",
        euLastUpdated: Timestamp.now(),
        euUpdatedBy: "system"
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(updateData);

      await updateDoc(entityRef, cleanedData);
      
      console.log(`Updated entity ${entityId} with EU data`);
      return entityId;
    } catch (error) {
      console.error('Error updating entity with EU data:', error);
      throw error;
    }
  }

  /**
   * Create new entity from EU disease (if no match found)
   */
  async createEntityFromEUDisease(organizationId, euDisease) {
    try {
      const { addDoc } = await import('firebase/firestore');
      const entitiesRef = collection(db, 'biological_entities');
      
      // Determine entity type from category
      const type = euDisease.category.toLowerCase();
      
      const newEntity = {
        organizationId,
        scientificName: euDisease.scientificName,
        commonName: euDisease.diseaseName,
        category: euDisease.category,
        type,
        
        // Basic RPN
        severityScore: this.estimateSeverityFromImportance(
          euDisease.publicHealthImportance
        ),
        occurrenceProbability: 5, // Default
        detectionDifficulty: {
          score: 5, // Default
          reasons: [],
          timeToResults: "varies",
          falseNegativeRisk: "medium"
        },
        rpnScore: this.estimateSeverityFromImportance(
          euDisease.publicHealthImportance
        ) * 5 * 5,
        
        // EU DATA
        euNotifiable: true,
        euDiseaseCode: euDisease.diseaseCode,
        euDecision: "2018/945",
        euCaseDefinition: euDisease.caseDefinition || null,
        euReportingDeadline: euDisease.reportingDeadline,
        euSurveillanceSystem: euDisease.surveillanceSystem || "TESSy",
        ecdcCode: euDisease.ecdcCode,
        euPublicHealthImportance: euDisease.publicHealthImportance,
        euTransmissionMode: euDisease.transmissionMode || [],
        euIncubationPeriod: euDisease.incubationPeriod || null,
        euIsolationRequired: euDisease.isolationRequired || false,
        euContactTracingRequired: euDisease.contactTracingRequired || false,
        euPreventionMeasures: euDisease.preventionMeasures || [],
        euRpnMultiplier: euDisease.rpnBaseMultiplier,
        euAlertThresholdMultiplier: euDisease.alertThresholdMultiplier,
        euOutbreakThreshold: euDisease.outbreakThreshold || null,
        euSeasonalPattern: euDisease.seasonalPattern || null,
        euDataSource: "EU Decision 2018/945",
        
        // FLAGS
        exampleDataFlag: "PREBUILT_EXAMPLE",
        exampleSource: "EU Decision 2018/945",
        isExampleData: true,
        
        // METADATA
        isActive: true,
        status: "Active",
        createdAt: Timestamp.now(),
        createdBy: "system",
        updatedAt: Timestamp.now(),
        updatedBy: "system"
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(newEntity);

      const docRef = await addDoc(entitiesRef, cleanedData);
      
      console.log(`Created new entity ${docRef.id} from EU disease`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating entity from EU disease:', error);
      throw error;
    }
  }

  /**
   * Helper: Estimate severity from public health importance
   */
  estimateSeverityFromImportance(importance) {
    const mapping = {
      'CRITICAL': 10,
      'HIGH': 8,
      'MEDIUM': 6,
      'LOW': 4
    };
    return mapping[importance] || 5;
  }

  /**
   * Get all EU notifiable diseases (mapped to biological entities)
   */
  async getEUNotifiableDiseases(organizationId) {
    try {
      const entitiesRef = collection(db, 'biological_entities');
      const q = query(
        entitiesRef,
        where('organizationId', '==', organizationId),
        where('euNotifiable', '==', true),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting EU diseases:', error);
      throw error;
    }
  }

  /**
   * Get all EU diseases from source data (EU Decision 2018/945)
   * Returns the complete list including unmapped diseases
   */
  getAllEUDiseasesFromSource() {
    return euDiseasesData.diseases || [];
  }

  /**
   * Get mapping status for each EU disease
   */
  async getEUDiseasesWithMappingStatus(organizationId) {
    try {
      // Get all diseases from source data
      const allDiseases = this.getAllEUDiseasesFromSource();
      
      // Get mapped diseases from Firestore
      const mappedDiseases = await this.getEUNotifiableDiseases(organizationId);
      
      // Create a map of mapped diseases by EU code
      const mappedMap = new Map();
      mappedDiseases.forEach(disease => {
        if (disease.euDiseaseCode) {
          mappedMap.set(disease.euDiseaseCode, disease);
        }
      });
      
      // Combine: show all source diseases with mapping status
      return allDiseases.map(sourceDisease => {
        const mapped = mappedMap.get(sourceDisease.diseaseCode);
        return {
          ...sourceDisease,
          isMapped: !!mapped,
          mappedEntityId: mapped?.id || null,
          mappedEntityName: mapped?.commonName || mapped?.scientificName || null
        };
      });
    } catch (error) {
      console.error('Error getting EU diseases with mapping status:', error);
      throw error;
    }
  }

  /**
   * Get diseases requiring immediate reporting (24 hours)
   */
  async getImmediateReportingDiseases(organizationId) {
    try {
      const entitiesRef = collection(db, 'biological_entities');
      const q = query(
        entitiesRef,
        where('organizationId', '==', organizationId),
        where('euNotifiable', '==', true),
        where('euReportingDeadline', '==', '24 hours'),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting immediate reporting diseases:', error);
      throw error;
    }
  }

  /**
   * Get EU disease statistics
   */
  async getEUDiseaseStatistics(organizationId) {
    try {
      const allEUDiseases = await this.getEUNotifiableDiseases(organizationId);
      
      return {
        total: allEUDiseases.length,
        byReportingDeadline: this.groupByReportingDeadline(allEUDiseases),
        byPublicHealthImportance: this.groupByImportance(allEUDiseases),
        requireIsolation: allEUDiseases.filter(d => d.euIsolationRequired).length,
        requireContactTracing: allEUDiseases.filter(d => d.euContactTracingRequired).length
      };
    } catch (error) {
      console.error('Error getting EU disease statistics:', error);
      throw error;
    }
  }

  /**
   * Helper: Group by reporting deadline
   */
  groupByReportingDeadline(diseases) {
    return diseases.reduce((acc, disease) => {
      const deadline = disease.euReportingDeadline || 'unknown';
      acc[deadline] = (acc[deadline] || 0) + 1;
      return acc;
    }, {});
  }

  /**
   * Helper: Group by importance
   */
  groupByImportance(diseases) {
    return diseases.reduce((acc, disease) => {
      const importance = disease.euPublicHealthImportance || 'unknown';
      acc[importance] = (acc[importance] || 0) + 1;
      return acc;
    }, {});
  }

  /**
   * Helper: Remove undefined values from object
   */
  removeUndefinedValues(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = this.removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
}

const euDiseaseServiceInstance = new EUDiseaseService();
export default euDiseaseServiceInstance;

